<template>
    <div class="forecast-context-picker">
        <component-loader v-if="loading" class="context-loading" />
        <p v-else-if="errorMessage" class="context-error">
            {{ errorMessage }}
        </p>
        <template v-else>
            <template v-if="isRunOutput">
                <div v-if="props.showInput && !props.fixedInputId" class="context-field">
                    <label for="forecast-context-input">Input</label>
                    <primevue-select
                        id="forecast-context-input"
                        v-model="selectedInputId"
                        :options="inputOptions"
                        option-label="name"
                        option-value="id"
                        :disabled="props.disabled"
                        filter
                        @update:model-value="selectInput"
                    />
                </div>
                <div class="context-field">
                    <label for="forecast-context-quarter">Quarter</label>
                    <primevue-select
                        id="forecast-context-quarter"
                        v-model="selectedQuarter"
                        :options="quarterOptions"
                        option-label="name"
                        option-value="id"
                        :disabled="props.disabled || !quarterOptions.length"
                        filter
                        @update:model-value="selectQuarter"
                    />
                </div>
                <div class="context-field">
                    <label for="forecast-context-scenario">Scenario</label>
                    <primevue-select
                        id="forecast-context-scenario"
                        v-model="selectedScenarioKey"
                        :options="scenarioOptions"
                        option-label="name"
                        option-value="id"
                        :disabled="props.disabled || !scenarioOptions.length"
                        filter
                    />
                </div>
                <div v-if="props.showPercentile" class="context-field">
                    <label for="forecast-context-percentile">Percentile</label>
                    <primevue-select
                        id="forecast-context-percentile"
                        v-model="selectedPercentile"
                        :options="percentileOptions"
                        option-label="name"
                        option-value="id"
                        :disabled="props.disabled || !percentileOptions.length"
                    />
                </div>
            </template>
            <template v-else>
                <div class="context-field">
                    <label for="forecast-context-market-quarter">Quarter</label>
                    <primevue-select
                        id="forecast-context-market-quarter"
                        v-model="selectedQuarter"
                        :options="quarterOptions"
                        option-label="name"
                        option-value="id"
                        :disabled="props.disabled || !quarterOptions.length"
                        filter
                        @update:model-value="selectQuarter"
                    />
                </div>
                <div class="context-field">
                    <label for="forecast-context-market-scenario">Scenario</label>
                    <primevue-select
                        id="forecast-context-market-scenario"
                        v-model="selectedScenarioKey"
                        :options="scenarioOptions"
                        option-label="name"
                        option-value="id"
                        :disabled="props.disabled || !scenarioOptions.length"
                        filter
                    />
                </div>
            </template>
            <div class="submit-button-holder">
                <prime-button
                    class="plain-btn flex-row-with-gap"
                    label="Apply"
                    :disabled="props.disabled"
                    @click="emitSelection"
                >
                    Update context options
                    <font-awesome-icon
                        class="icon"
                        :icon="['fas', 'sync']"
                    />
                </prime-button>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import PrimevueSelect from 'primevue/select';
import {
    buildStorageForecastAssetContextOptions,
    buildStorageForecastAssumptionContextOptions,
    findStorageForecastAssumptionContextOption,
    findStorageForecastRunContextOption,
    getUniqueStorageForecastInputs,
    getUniqueStorageForecastQuarters,
} from '@/utils/storageForecasting/storageForecastContextUtils.ts';
import type {
    IStorageForecastAssumptionContextOption,
    IStorageForecastContextSelection,
    IStorageForecastRunContextOption,
    TStorageForecastReportSourceType,
} from '@/utils/storageForecasting/storageForecastContextUtils.ts';
import { getAssetForecastPercentileOptions } from '@/utils/storageForecasting/storageForecastAssetReportUtils.ts';
import PrimeButton from 'primevue/button';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import { type TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';
import { useAssetStore } from '@/stores/storageForecasting/assetStore.ts';

const props = withDefaults(defineProps<{
    sourceType: TStorageForecastReportSourceType;
    modelValue?: IStorageForecastContextSelection | null;
    fixedInputId?: string;
    fixedCountry?: TStorageForecastCountries;
    showInput?: boolean;
    showPercentile?: boolean;
    disabled?: boolean;
}>(), {
    modelValue: null,
    fixedInputId: undefined,
    fixedCountry: undefined,
    showInput: false,
    showPercentile: false,
    disabled: false,
});

const emit = defineEmits<{
    'update:modelValue': [IStorageForecastContextSelection];
}>();

const assetStore = useAssetStore();
const runContextOptions = ref<Array<IStorageForecastRunContextOption>>([]);
const assumptionContextOptions = ref<Array<IStorageForecastAssumptionContextOption>>([]);
const selectedInputId = ref<string>('');
const selectedCountry = ref<TStorageForecastCountries | null>(null);
const selectedQuarter = ref<string>('');
const selectedScenarioKey = ref<string>('');
const selectedPercentile = ref<string>('mean');
const loading = ref<boolean>(false);
const errorMessage = ref<string>('');
const hydrating = ref<boolean>(false);

const isRunOutput = computed<boolean>(() => props.sourceType === 'RunOutput');
const selectedInputIdForFilter = computed<string>(() => props.fixedInputId || (props.showInput ? selectedInputId.value : ''));
const visibleInputIds = computed<Set<string>>(() => new Set(assetStore.visibleAvailableInputs.map(input => input.id)));
const hydratedInputId = computed<string>(() => props.fixedInputId || props.modelValue?.inputId || selectedInputId.value);
const inputOptions = computed(() => getUniqueStorageForecastInputs(runContextOptions.value).filter(option => (
    visibleInputIds.value.has(option.id) || option.id === hydratedInputId.value
)));
const runOptionsForSelection = computed<Array<IStorageForecastRunContextOption>>(() => {
    const inputId = selectedInputIdForFilter.value;
    const isHydratingHiddenInput = !!inputId && !visibleInputIds.value.has(inputId);
    const availableOptions = isHydratingHiddenInput
        ? runContextOptions.value
        : runContextOptions.value.filter(option => visibleInputIds.value.has(option.inputId));

    return availableOptions.filter(option => (
        (!selectedCountry.value || option.country === selectedCountry.value)
        && (!inputId || option.inputId === inputId)
    ));
});
const assumptionOptionsForCountry = computed<Array<IStorageForecastAssumptionContextOption>>(() => assumptionContextOptions.value.filter(option => (
    !selectedCountry.value || option.country === selectedCountry.value
)));
const quarterOptions = computed(() => (
    isRunOutput.value
        ? getUniqueStorageForecastQuarters(runOptionsForSelection.value)
        : getUniqueStorageForecastQuarters(assumptionOptionsForCountry.value)
));
const runScenarioContextOptions = computed<Array<IStorageForecastRunContextOption>>(() => runOptionsForSelection.value.filter(option => (
    !selectedQuarter.value || option.quarter === selectedQuarter.value
)));
const assumptionScenarioContextOptions = computed<Array<IStorageForecastAssumptionContextOption>>(() => assumptionOptionsForCountry.value.filter(option => (
    !selectedQuarter.value || option.quarter === selectedQuarter.value
)));
const scenarioOptions = computed<Array<{ id: string; name: string }>>(() => {
    const seen = new Set<string>();
    const sourceOptions = isRunOutput.value
        ? runScenarioContextOptions.value.map(option => ({
            id: selectedInputIdForFilter.value ? option.scenarioId : option.scenarioName,
            name: option.scenarioName,
        }))
        : assumptionScenarioContextOptions.value.map(option => ({
            id: option.scenarioId,
            name: option.scenarioName,
        }));

    return sourceOptions.filter((option) => {
        const key = option.id || option.name;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
});
const percentileOptions = computed(() => getAssetForecastPercentileOptions());
const selectedRunContext = computed<IStorageForecastRunContextOption | null>(() => {
    const context = selectedScenarioKey.value
        ? {
            country: selectedCountry.value,
            inputId: selectedInputIdForFilter.value || undefined,
            quarter: selectedQuarter.value,
            ...(selectedInputIdForFilter.value
                ? { scenarioId: selectedScenarioKey.value }
                : { scenarioName: selectedScenarioKey.value }),
        }
        : getSingleContextModelValue();
    const selectedOption = findStorageForecastRunContextOption(runContextOptions.value, context, props.fixedInputId);
    return selectedOption
        ? { ...selectedOption, percentile: props.showPercentile ? selectedPercentile.value : undefined }
        : null;
});
const selectedAssumptionContext = computed<IStorageForecastAssumptionContextOption | null>(() => findStorageForecastAssumptionContextOption(
    assumptionContextOptions.value,
    {
        country: selectedCountry.value,
        quarter: selectedQuarter.value,
        scenarioId: selectedScenarioKey.value,
    },
));

function getSingleContextModelValue (): IStorageForecastContextSelection | null {
    return props.modelValue as IStorageForecastContextSelection | null;
}

function setHydratedSelection (update: () => void): void {
    hydrating.value = true;
    update();
    hydrating.value = false;
}

function normaliseRunSelection (): void {
    if (props.showInput && !props.fixedInputId && !selectedInputId.value) {
        selectedInputId.value = inputOptions.value[0]?.id ?? '';
    }

    if (!quarterOptions.value.some(option => option.id === selectedQuarter.value)) {
        selectedQuarter.value = quarterOptions.value[0]?.id ?? '';
    }

    if (!scenarioOptions.value.some(option => option.id === selectedScenarioKey.value)) {
        selectedScenarioKey.value = scenarioOptions.value[0]?.id ?? '';
    }

    if (!percentileOptions.value.some(option => option.id === selectedPercentile.value)) {
        selectedPercentile.value = percentileOptions.value[0]?.id ?? 'mean';
    }
}

function normaliseAssumptionSelection (): void {
    if (!quarterOptions.value.some(option => option.id === selectedQuarter.value)) {
        selectedQuarter.value = quarterOptions.value[0]?.id ?? '';
    }

    if (!scenarioOptions.value.some(option => option.id === selectedScenarioKey.value)) {
        selectedScenarioKey.value = scenarioOptions.value[0]?.id ?? '';
    }
}

function hydrateSelection (): void {
    setHydratedSelection(() => {
        if (isRunOutput.value) {
            const modelValue = getSingleContextModelValue();
            const matchedContext = findStorageForecastRunContextOption(runContextOptions.value, modelValue, props.fixedInputId);
            selectedCountry.value = props.fixedCountry ?? matchedContext?.country ?? modelValue?.country ?? null;
            selectedInputId.value = props.fixedInputId || matchedContext?.inputId || (props.showInput ? inputOptions.value[0]?.id : '') || '';
            selectedQuarter.value = matchedContext?.quarter || modelValue?.quarter || '';
            selectedScenarioKey.value = selectedInputIdForFilter.value
                ? matchedContext?.scenarioId || modelValue?.scenarioId || ''
                : matchedContext?.scenarioName || modelValue?.scenarioName || modelValue?.scenarioLabel || '';
            selectedPercentile.value = modelValue?.percentile || 'mean';
            normaliseRunSelection();
            return;
        }

        const modelValue = getSingleContextModelValue();
        const matchedContext = findStorageForecastAssumptionContextOption(assumptionContextOptions.value, modelValue);
        selectedCountry.value = props.fixedCountry ?? (matchedContext?.country || modelValue?.country || null);
        selectedQuarter.value = matchedContext?.quarter || modelValue?.quarter || '';
        selectedScenarioKey.value = matchedContext?.scenarioId || modelValue?.scenarioId || '';
        normaliseAssumptionSelection();
    });
}

function emitSelection (): void {
    if (hydrating.value) return;

    const selectedContext = isRunOutput.value ? selectedRunContext.value : selectedAssumptionContext.value;
    if (selectedContext) emit('update:modelValue', selectedContext);
}

function selectInput (): void {
    normaliseRunSelection();
}

function selectQuarter (): void {
    if (isRunOutput.value) {
        normaliseRunSelection();
    } else {
        normaliseAssumptionSelection();
    }
}

async function loadOptions (): Promise<void> {
    loading.value = true;
    errorMessage.value = '';
    try {
        if (props.sourceType === 'RunOutput') {
            await assetStore.ensureAllAvailableInputs();
            runContextOptions.value = buildStorageForecastAssetContextOptions(assetStore.allAvailableInputs);
            assumptionContextOptions.value = [];
        } else {
            await assetStore.ensurePowerCurvesAssumptionsList();
            assumptionContextOptions.value = buildStorageForecastAssumptionContextOptions(assetStore.powerCurvesAssumptionsList, props.sourceType);
            runContextOptions.value = [];
        }
        hydrateSelection();
    } catch {
        errorMessage.value = 'Unable to load forecast options.';
    } finally {
        loading.value = false;
    }
}

watch(
    () => [props.sourceType, props.fixedInputId],
    loadOptions,
    { immediate: true },
);

watch(
    () => props.fixedCountry,
    hydrateSelection,
);

watch(
    () => props.modelValue,
    hydrateSelection,
    { deep: true },
);
</script>

<style scoped lang="scss">
.forecast-context-picker {
    display: grid;
    gap: var(--defaultSpacing);
    min-width: min(34rem, 80vw);
    min-height: 5rem;
    padding: var(--defaultSpacing);
}

.context-field {
    display: grid;
    grid-template-columns: 7rem minmax(14rem, 1fr);
    align-items: center;
    gap: var(--defaultSpacing);

    label {
        color: var(--secondaryFont);
        font-size: var(--smallFontSize);
    }
}

.context-loading,
.context-error {
    margin: 0;
    color: var(--secondaryFont);
    font-size: var(--smallFontSize);
}

.context-error {
    color: var(--dangerRed);
}

.submit-button-holder {
    display: flex;
    justify-content: flex-end;
    margin-top: var(--defaultSpacing);
}
</style>
